import { sla } from '@staiter/config';
import * as m from '@staiter/i18n';
import { PROJECT_STAGE_LABEL } from '@staiter/shared';
import type { ApprovalItem } from '@staiter/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
// S6 — Approvals queue with SLA indicators, diff view, approve/reject.
// Sprint N+ 2026-05-23: FE-44 i18n task labels, FE-45 StatusBadge,
// FE-49 SLA hierarchy, FE-50 row-click (remove «Открыть» button),
// FE-51 Tier 3 danger banner, FE-53 empty-state.
import { createFileRoute } from '@tanstack/react-router';
import { AlertTriangle, BellRing, ChevronRight, Lock, RefreshCw, ShieldCheck } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { AccessGrantorsInline } from '~/components/access/AccessGrantors';
import { RequestAccessButton } from '~/components/access/RequestAccessButton';
import { Tier3ApprovalBlock, Tier3CosignerPicker } from '~/components/approvals';
import { ListPageShell, PageHeader, Table } from '~/components/data';
import { AIInsightCard, ApprovalDiff } from '~/components/domain';
import { ApprovalImpactDrift } from '~/components/domain/ApprovalImpactDrift';
import { ApprovalRoute } from '~/components/domain/ApprovalRoute';
import { ApproverPicker } from '~/components/domain/project/ApproverPicker';
import {
  CancelImpactList,
  type CancelImpactSnapshot,
} from '~/components/domain/project/CancelImpactList';
import { approvalActionLabel } from '~/components/domain/project/PendingApprovalBadge';
import { useToast } from '~/components/feedback';
import { Button, Dialog, Drawer, Select, Textarea } from '~/components/primitives';
import { ProvenanceChip, StatusBadge } from '~/components/status';
import { TaskMigrationBanner } from '~/components/tasks/notifications/TaskMigrationBanner';
import {
  reassignApprovalRequest,
  remindApprovalRequest,
  withdrawApprovalRequest,
} from '~/lib/api/approval-requests';
import {
  type ApprovalsFilter,
  approveApproval,
  fetchApproval,
  fetchApprovals,
  mergeImpactRebuild,
  reassignTier3Request,
  rebuildApprovalImpact,
  rejectApproval,
  remindTier3Request,
  withdrawTier3Request,
} from '~/lib/api/approvals';
import { ApiRequestError } from '~/lib/api/client';
import { remindState } from '~/lib/approvals/remind';
import { tier3SubjectHref } from '~/lib/approvals/tier3';
import { formatDate } from '~/lib/format';
import { useListPageState } from '~/lib/hooks/useListPageState';
import { outcomeBadge, stageBadge } from '~/lib/projects/badge-tone';
import { getRoleLabel } from '~/lib/rbac';
import { useUserScope } from '~/lib/rbac/use-user-scope';
import { PROJECT_STATUS_LABEL } from '~/lib/tasks/i18n/labels';
import { useActiveEntityStore } from '~/store/active-entity';
import { useAuthStore } from '~/store/auth';
import shared from './_auth._shared.module.css';
import styles from './_auth.approvals.module.css';

export const Route = createFileRoute('/_auth/approvals')({
  staticData: { minMainWidth: 960 },
  component: ApprovalsInbox,
});

// Fallback SLA thresholds (TODO(S6+): загружать из @staiter/config/sla.v1.json).
const DEFAULT_SLA = { warning_after_hours: 4, critical_after_hours: 24 } as const;

function slaLevel(
  ageHours: number,
  sla: ApprovalItem['sla'] | undefined,
): 'ok' | 'warn' | 'danger' {
  const s = sla ?? DEFAULT_SLA;
  if (ageHours >= s.critical_after_hours) return 'danger';
  if (ageHours >= s.warning_after_hours) return 'warn';
  return 'ok';
}

// FE-45: map approval status to StatusBadge primitive variant.
function approvalStatusToBadge(status: ApprovalItem['status']): {
  status: 'needs_review' | 'completed_draft' | 'blocked' | 'escalated';
  label: string;
} {
  switch (status) {
    case 'waiting':
    case 'pending':
      return { status: 'needs_review', label: m.approval_status_waiting() };
    case 'executing':
      return { status: 'needs_review', label: m.approval_status_executing() };
    case 'approved':
      return { status: 'completed_draft', label: m.approval_status_approved() };
    case 'rejected':
      return { status: 'blocked', label: m.approval_status_rejected() };
    case 'expired':
      return { status: 'escalated', label: m.approval_status_expired() };
    case 'withdrawn':
      return { status: 'escalated', label: m.approval_status_withdrawn() };
    default:
      return { status: 'needs_review', label: String(status) };
  }
}

/**
 * F3 (tier2-approval-requests §5) — три разных намерения на одном экране.
 *
 * До пака очередь была построена под общий список без адресата: кто должен решить —
 * не видно, значит «моё» и «чужое» неразличимы. Вкладки разводят ровно эти три
 * вопроса: что ждёт МЕНЯ, что отправил Я, и что уже решено.
 *
 * История отдельной вкладкой, а не фильтром статуса: отозванная и истёкшая заявки
 * обязаны быть видны — пропавшая заявка неотличима от несозданной.
 */
type QueueTab = 'inbox' | 'outbox' | 'history';

const TAB_FILTER: Record<QueueTab, ApprovalsFilter> = {
  inbox: { status: 'pending', role: 'addressee' },
  outbox: { status: 'pending', role: 'requester' },
  history: { status: 'history' },
};

/**
 * Гейт фазы B — путь инициатора после отправки.
 *
 * Метка «ждёт согласования» на карточке проекта ведёт СЮДА со ссылкой на заявку:
 * без этого у инициатора после отправки не оставалось ни одного хода — меню
 * закрыто причиной, а переадресация и отзыв живут на карточке заявки.
 */
function requestIdFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  return new URLSearchParams(window.location.search).get('request');
}

/**
 * Заход 4 гейта — разница читается по-русски целиком, а не наполовину.
 *
 * Бэкенд отдаёт структуру (`outcome`, `archive`, `stage`, `plan_*`) и коды
 * состояний. Подписи и значения переводит фронт: словарь состояний проекта у него
 * уже есть, и вторая копия в бэкенде разошлась бы с ним при первой же правке.
 * Функцией, а не объектом-словарём: барьер `no-inline-strings` справедливо не
 * пускает словари подписей в компонент.
 */
function diffFieldLabel(key: string): string {
  switch (key) {
    case 'outcome':
      return m.approval_diff_field_outcome();
    case 'archive':
      return m.approval_diff_field_archive();
    case 'stage':
      return m.approval_diff_field_stage();
    case 'plan_start':
      return m.approval_diff_field_plan_start();
    case 'plan_end':
      return m.approval_diff_field_plan_end();
    default:
      return key;
  }
}

/**
 * T2A-F12(а) пост-деплойного гейта пака `tier2-approval-requests`, UX-половина:
 * «величина сдвига („−29 дней“) не показана рядом с разницей, и очередь не
 * триажится — по колонкам видно только `project_plan_shift`, суть изменения без
 * открытия карточки не видна».
 *
 * Величина считается из самой разницы, а не приходит отдельным полем: заявка уже
 * несёт `plan_start`/`plan_end` до и после, и второй источник той же величины
 * разошёлся бы с первым при первой же правке. Считается по НАЧАЛУ плана, а при
 * его отсутствии — по концу: сдвигают обычно оба края на одну величину, и
 * показать «сдвиг на N дней» честнее, чем молчать, если сдвинут только один.
 */
function planShiftDays(
  diff: {
    before?: Record<string, unknown>;
    after?: Record<string, unknown>;
  } | null,
): number | null {
  if (!diff?.before || !diff?.after) return null;
  for (const key of ['plan_start', 'plan_end'] as const) {
    const b = diff.before[key];
    const a = diff.after[key];
    if (typeof b !== 'string' || typeof a !== 'string') continue;
    const bt = Date.parse(b);
    const at = Date.parse(a);
    if (!Number.isFinite(bt) || !Number.isFinite(at)) continue;
    const days = Math.round((at - bt) / 86_400_000);
    if (days !== 0) return days;
  }
  return null;
}

function diffFieldLabels(
  after: Record<string, unknown>,
  before: Record<string, unknown>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const key of new Set([...Object.keys(before), ...Object.keys(after)])) {
    out[key] = diffFieldLabel(key);
  }
  return out;
}

/** Значение разницы: код состояния — словом, дата — датой, пустое — прочерком. */
function diffValue(key: string, raw: unknown): unknown {
  if (raw === null || raw === undefined || raw === '') return '—';
  const code = String(raw);
  if (key === 'archive') {
    return code === 'archived' ? m.approval_diff_archived() : m.approval_diff_not_archived();
  }
  if (key === 'plan_start' || key === 'plan_end') return formatDate(code);
  if (key === 'outcome' || key === 'stage') {
    return (
      PROJECT_STATUS_LABEL[code as keyof typeof PROJECT_STATUS_LABEL] ??
      PROJECT_STAGE_LABEL[code as keyof typeof PROJECT_STAGE_LABEL] ??
      code
    );
  }
  return raw;
}

/**
 * T2A-F15 (F29) — значение ячейки «Разницы». Исход и стадия — статусным бейджем с
 * тоном реестра (оценку несёт токен, а не заливка ячейки); прочее — словом.
 */
function renderDiffValue(key: string, raw: unknown): ReactNode {
  if (raw !== null && raw !== undefined && raw !== '') {
    const code = String(raw);
    if (key === 'outcome') {
      const b = outcomeBadge(code);
      return <StatusBadge status={b.tone} label={b.label} size="md" />;
    }
    if (key === 'stage' && code in PROJECT_STAGE_LABEL) {
      const b = stageBadge(code as keyof typeof PROJECT_STAGE_LABEL);
      return <StatusBadge status={b.tone} label={b.label} size="md" />;
    }
  }
  return String(diffValue(key, raw) ?? '—');
}

function PersonCell({
  user,
  fallbackId,
}: {
  user?: { name?: string | null; email?: string } | undefined;
  fallbackId: string;
}): JSX.Element {
  const name = user?.name || null;
  const email = user?.email || null;
  if (!name && !email) return <span className={styles.cellCode}>{fallbackId.slice(0, 8)}…</span>;
  return (
    <div className={styles.cellStack}>
      <span>{name ?? email}</span>
      {name && email && <span className={styles.cellCode}>{email}</span>}
    </div>
  );
}

function ApprovalsInbox(): JSX.Element {
  const [tab, setTab] = useState<QueueTab>('inbox');
  const [filter, setFilter] = useState<ApprovalsFilter>(TAB_FILTER.inbox);
  const [selectedId, setSelectedId] = useState<string | null>(requestIdFromUrl());
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState('');
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignTo, setReassignTo] = useState('');
  const qc = useQueryClient();
  const toast = useToast();
  const currentUser = useAuthStore((s) => s.user);
  /**
   * Идентификатор текущего пользователя приходит в `user_id`, а не в `id`.
   *
   * Живой прогон поймал ровно это: кнопка «Согласовать и исполнить» была ВЫКЛЮЧЕНА у
   * настоящего адресата с подписью «заявка адресована другому человеку». Остальной
   * фронт читает поле через `user_id ?? id` (`useAuthStore` в диалогах проекта) —
   * здесь читалось только `id`, и сравнение с адресатом всегда было ложным.
   */
  const currentUserId = (currentUser?.user_id ?? currentUser?.id ?? '') as string;

  const q = useQuery({
    queryKey: ['approvals', filter],
    queryFn: () => fetchApprovals(filter),
    staleTime: 15_000,
  });
  /**
   * Заявка по ссылке грузится ОТДЕЛЬНО от списка: инициатор приходит с карточки
   * проекта, а его заявка лежит во вкладке «мои отправленные», не в «мне на решение».
   * Без этого ссылка открывала пустой ящик — тупик того же рода, что и раньше.
   */
  const linkedId = requestIdFromUrl();
  const linkedQuery = useQuery({
    queryKey: ['approval-linked', linkedId],
    queryFn: () => fetchApproval(linkedId as string),
    enabled: !!linkedId,
    staleTime: 5_000,
  });
  /**
   * Заход 4 гейта — ссылка открывает ТУ вкладку, где заявка лежит.
   *
   * Инициатор приходил на «мне на решение» и видел под своей открытой заявкой
   * пустой список с надписью «согласований не найдено». Заявка есть, список
   * говорит, что нет — экран противоречит сам себе на первом же кадре.
   */
  const linkedTabApplied = useRef<string | null>(null);
  useEffect(() => {
    const row = linkedQuery.data as
      | { id?: string; status?: string; addressee_user_id?: string | null }
      | undefined;
    // F92 (Ф6): вкладка выбирается, когда уже известно, кто смотрит. Раньше ответ ссылки
    // приходил раньше /auth/me, и адресат попадал во «Мои отправленные» с пустым списком.
    if (!row?.id || !linkedId || !currentUserId || linkedTabApplied.current === linkedId) return;
    linkedTabApplied.current = linkedId;
    const pending = row.status === 'pending' || row.status === 'waiting';
    const next: QueueTab = !pending
      ? 'history'
      : row.addressee_user_id && row.addressee_user_id === currentUserId
        ? 'inbox'
        : 'outbox';
    setTab(next);
    setFilter((f) => ({ ...TAB_FILTER[next], ...(f.task_type ? { task_type: f.task_type } : {}) }));
  }, [linkedQuery.data, linkedId, currentUserId]);

  const switchTab = (next: QueueTab): void => {
    setTab(next);
    // Фильтр типа сохраняется между вкладками — он про предмет, а не про адресность.
    setFilter((f) => ({ ...TAB_FILTER[next], ...(f.task_type ? { task_type: f.task_type } : {}) }));
    setSelectedId(null);
  };
  const state = useListPageState(q);

  // Post-design-review: default sort tier desc → created_at asc — Tier 3 наверху.
  const items = (q.data?.items ?? []).slice().sort((a, b) => {
    if (a.tier !== b.tier) return b.tier - a.tier;
    const aTime = a.requested_at ? new Date(a.requested_at).getTime() : 0;
    const bTime = b.requested_at ? new Date(b.requested_at).getTime() : 0;
    return aTime - bTime;
  });
  const selectedSummary =
    items.find((x) => x.id === selectedId) ??
    (selectedId && linkedQuery.data?.id === selectedId
      ? (linkedQuery.data as unknown as (typeof items)[number])
      : null);
  // F-sync — колонка «Компания» только в агрегатном охвате (холдинг/«Все компании»).
  const aggregateScope = useActiveEntityStore((s) => s.activeScope) !== 'entity';
  const entities = q.data?.entities ?? {};

  // FE-12/13: fetch full detail (с diff + evidence_refs + collected_signatures) когда drawer
  // открыт. List endpoint detail-shape отсутствует — нужен GET /approvals/:id.
  const detailQuery = useQuery({
    queryKey: ['approval', selectedId],
    queryFn: () => fetchApproval(selectedId as string),
    enabled: !!selectedId,
    staleTime: 5_000,
  });
  // Merge: detail wins (richer fields), fallback to list summary. Cast type через summary
  // shape (id обязателен из selectedSummary).
  const selected =
    selectedId && selectedSummary
      ? ({ ...selectedSummary, ...(detailQuery.data ?? {}) } as typeof selectedSummary &
          Partial<NonNullable<typeof detailQuery.data>>)
      : null;

  /**
   * Заход 4 гейта — «кто решает» и «почему кнопки выключены» считаются ОДИН раз и
   * живут на том же экране, где стоят кнопки.
   *
   * До этого адресность вычислялась внутри футера, и тело карточки о ней не знало:
   * читатель видел выключенную кнопку без причины и не видел имени того, за кем
   * решение. Выключенная кнопка без имени ответственного — тупик, а не защита.
   */
  const requestAddresseeId =
    (selected as { addressee_user_id?: string | null } | null)?.addressee_user_id ?? null;
  const isRequest = Boolean(requestAddresseeId);
  const addressedToMe = !!currentUserId && requestAddresseeId === currentUserId;
  const requestedByMe =
    !!currentUserId &&
    (selected as { requested_by_user_id?: string | null } | null)?.requested_by_user_id ===
      currentUserId;
  /**
   * Заход 5 — есть ли у читателя доступ к модулю проектов.
   *
   * У роли `reviewer` модуля `projects` нет: она решает заявки, а карточки проектов
   * не открывает. Ссылка «Открыть проект» вела бы её в 403 в самый неподходящий
   * момент — в момент решения.
   */
  /** F92 (Ф6): заявка уровня 3 — две подписи двух зон, своё управление и свои причины. */
  const isTier3 = selected?.tier === 3;
  const moduleAccess = useUserScope().module_access;
  const mayOpenSubject = moduleAccess === undefined || moduleAccess.includes('projects');
  /** Имя адресата: из карточки, затем из денорм-набора списка, и лишь потом id. */
  const addresseeLabel = ((): string | null => {
    if (!requestAddresseeId) return null;
    const fromDetail = (selected as { addressee_name?: string | null } | null)?.addressee_name;
    if (fromDetail) return fromDetail;
    const fromList = q.data?.users?.[requestAddresseeId] as
      | { name?: string | null; email?: string }
      | undefined;
    return fromList?.name || fromList?.email || `${requestAddresseeId.slice(0, 8)}…`;
  })();

  /**
   * F4 — согласование ИСПОЛНЯЕТ намерение, и оно может не примениться: предпосылки
   * разошлись за время ожидания. Экран обязан сказать это словами, а не общей
   * ошибкой — иначе «согласовал, и ничего не произошло» выглядит как сбой.
   */
  const actor = {
    userId: currentUserId,
    role: (currentUser?.role as string | undefined) ?? 'reviewer',
  };
  /**
   * F28 (T2A-F14) — пересборка снимка последствий. Ответ кладётся прямо в карточку:
   * блок решения снимается сразу, без перезагрузки и без ожидания повторного GET.
   */
  const rebuildMut = useMutation({
    mutationFn: (id: string) => rebuildApprovalImpact(id),
    // Свой тост ниже — глобальный «Ошибка загрузки» был бы вторым об одном.
    meta: { suppressErrorToast: true },
    onSuccess: (r) => {
      qc.setQueryData(['approval', r.id], (old: unknown) => mergeImpactRebuild(old, r));
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      toast.push({ variant: 'success', title: m.approval_impact_rebuilt_toast() });
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.approval_impact_rebuild_failed(),
        description: err.message,
      }),
  });
  const approveMut = useMutation({
    mutationFn: (id: string) => approveApproval(id, actor),
    // F28: у отказа свой тост (расхождение последствий, неисполнение) — глобальный
    // «Ошибка загрузки» вставал вторым поверх него.
    meta: { suppressErrorToast: true },
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.route_approvals_toast_approved() });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['project'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      setSelectedId(null);
    },
    onError: (err: Error, id: string) => {
      // F28: последствия разошлись, пока карточка была открыта. Заявка НЕ закрыта —
      // её надо пересобрать; общий текст «заявка закрыта» сказал бы неправду.
      // F92 (Ф6, §9.4 п. 5): без 1С вторая подпись не ставится — заявка ждёт, подпись не
      // записана. Это не отказ и не ошибка: человеку нужен срок, а не красный тост.
      if (err instanceof ApiRequestError && err.code === 'tier3.integration_not_connected') {
        toast.push({ variant: 'warning', title: m.tier3_card_block_offline() });
        void qc.invalidateQueries({ queryKey: ['approval', id] });
        return;
      }
      if (err instanceof ApiRequestError && err.code === 'APPROVAL_IMPACT_CHANGED') {
        toast.push({
          variant: 'warning',
          title: m.approval_impact_changed_toast_title(),
          description: m.approval_impact_changed_blocked(),
        });
        void qc.invalidateQueries({ queryKey: ['approval', id] });
        return;
      }
      // Отказ по расхождению предпосылок — не «ошибка сервера»: заявка закрыта,
      // объект не тронут, и человеку нужно знать именно это.
      toast.push({
        variant: 'error',
        title: m.approval_decide_failed_title(),
        description: `${m.approval_decide_failed_hint()} · ${err.message}`,
      });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
    },
  });

  const rejectMut = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => rejectApproval(id, actor, note),
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.route_approvals_toast_rejected() });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      setRejectOpen(false);
      setRejectNote('');
      setSelectedId(null);
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.route_approvals_toast_reject_failed(),
        description: err.message,
      }),
  });

  /** F5 — адресат недоступен: инициатор переводит заявку другому кандидату. */
  const reassignMut = useMutation({
    mutationFn: async ({ id, to }: { id: string; to: string }): Promise<void> => {
      if (isTier3) await reassignTier3Request(id, to);
      else await reassignApprovalRequest(id, { addressee_user_id: to });
    },
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.approval_reassign_cta() });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      setReassignOpen(false);
      setReassignTo('');
      setSelectedId(null);
    },
    onError: (err: Error) =>
      toast.push({ variant: 'error', title: m.approval_reassign_cta(), description: err.message }),
  });

  /** F6 — передумал: отзыв собственного намерения подписи не требует. */
  /**
   * F30 (T2A-F16) — «напомнить решающему». Ответ ставит отметку в карточку сразу:
   * кнопка гаснет с причиной без повторного запроса.
   */
  const remindMut = useMutation({
    mutationFn: (id: string) => (isTier3 ? remindTier3Request(id) : remindApprovalRequest(id)),
    meta: { suppressErrorToast: true },
    onSuccess: (r) => {
      qc.setQueryData(['approval', r.id], (old: unknown) =>
        old && typeof old === 'object' ? { ...old, last_reminded_at: r.reminded_at } : old,
      );
      toast.push({ variant: 'success', title: m.approval_remind_done() });
    },
    onError: (err: Error, id: string) => {
      if (err instanceof ApiRequestError && err.code === 'APPROVAL_REMIND_TOO_SOON') {
        const next = err.details?.next_allowed_at;
        toast.push({
          variant: 'info',
          title: m.approval_remind_too_soon({
            date: typeof next === 'string' ? formatDate(next) : '—',
          }),
        });
        void qc.invalidateQueries({ queryKey: ['approval', id] });
        return;
      }
      toast.push({
        variant: 'error',
        title: m.approval_remind_failed(),
        description: err.message,
      });
    },
  });
  const withdrawMut = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      if (isTier3) await withdrawTier3Request(id);
      else await withdrawApprovalRequest(id);
    },
    onSuccess: () => {
      toast.push({ variant: 'success', title: m.approval_withdraw_cta() });
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      void qc.invalidateQueries({ queryKey: ['project'] });
      void qc.invalidateQueries({ queryKey: ['projects'] });
      setSelectedId(null);
    },
    onError: (err: Error) =>
      toast.push({ variant: 'error', title: m.approval_withdraw_cta(), description: err.message }),
  });
  /**
   * С-G Р.9 (акт С-F) — «Пересобрать заявку»: отзыв устаревшей заявки и переход к документу.
   * Раньше — голый `withdrawTier3Request(...).then` без обработки ошибки: отказ сервера (заявку
   * уже решили, нет прав) терялся молча, кнопка не крутилась и перехода не было.
   */
  const staleRebuildMut = useMutation({
    mutationFn: async (args: { id: string; href: string | null }) => {
      await withdrawTier3Request(args.id, 'stale');
      return args.href;
    },
    onSuccess: (href) => {
      void qc.invalidateQueries({ queryKey: ['approvals'] });
      if (href) window.location.assign(href);
    },
    onError: (err: Error) =>
      toast.push({
        variant: 'error',
        title: m.tier3_card_rebuild(),
        description:
          err instanceof ApiRequestError
            ? m.error_by_code(err.code, err.details ?? {})
            : m.tier3_send_reason_unavailable(),
      }),
  });

  return (
    <div className={shared.page}>
      <PageHeader title={m.route_approvals_title()} description={m.route_approvals_description()} />
      {/* F3 — вкладки: «мне на решение», «мои отправленные», «история». */}
      <div className={styles.queueTabs} role="tablist" aria-label={m.route_approvals_title()}>
        {(
          [
            ['inbox', m.approval_queue_tab_inbox()],
            ['outbox', m.approval_queue_tab_outbox()],
            ['history', m.approval_queue_tab_history()],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={styles.queueTab}
            data-active={tab === id}
            onClick={() => switchTab(id)}
          >
            {label}
          </button>
        ))}
      </div>
      <TaskMigrationBanner targetViewId="sys-approvals" what="Очередь согласований" />

      <section className={shared.sectionCard}>
        <h3 className={shared.sectionHeading}>{m.route_approvals_filters_heading()}</h3>
        <div className={shared.formGrid2}>
          <Select
            label={m.route_approvals_filter_task_type()}
            value={filter.task_type ?? ''}
            onChange={(e) =>
              setFilter((f) => ({
                ...f,
                task_type: (e.target.value || undefined) as typeof f.task_type,
              }))
            }
            options={[
              { value: '', label: m.route_approvals_filter_all() },
              { value: 'run_result', label: m.route_approvals_task_run_result() },
              { value: 'stock_entry_submit', label: m.route_approvals_task_stock_entry() },
              {
                value: 'stock_reconciliation_submit',
                label: m.route_approvals_task_stock_recon(),
              },
              { value: 'work_order_transition', label: m.route_approvals_task_wo_transition() },
              { value: 'landed_cost_apply', label: m.route_approvals_task_landed_cost() },
              { value: 'regulatory_push', label: m.route_approvals_task_regulatory() },
            ]}
          />
          <Select
            label={m.route_approvals_filter_status()}
            value={filter.status ?? ''}
            onChange={(e) =>
              setFilter((f) => ({
                ...f,
                status: (e.target.value || undefined) as typeof f.status,
              }))
            }
            options={[
              { value: '', label: m.route_approvals_filter_all() },
              { value: 'pending', label: m.route_approvals_status_pending() },
              { value: 'approved', label: m.route_approvals_status_approved() },
              { value: 'rejected', label: m.route_approvals_status_rejected() },
              { value: 'expired', label: m.route_approvals_status_expired() },
            ]}
          />
        </div>
      </section>

      <ListPageShell
        state={state}
        // FE-53 Sprint Q+: filter-zero-state vs no-data-at-all distinction.
        emptyTitle={
          filter.task_type || filter.status
            ? m.approvals_empty_filter_title()
            : m.route_approvals_empty_title()
        }
        emptyDescription={m.route_approvals_empty_description()}
        emptyAction={
          filter.task_type || filter.status ? (
            <Button variant="secondary" onClick={() => setFilter({ status: 'pending' })}>
              {m.approvals_empty_filter_action()}
            </Button>
          ) : undefined
        }
      >
        {(rows) => (
          <Table
            ariaLabel={m.route_approvals_table_aria()}
            data={rows}
            rowKey={(r) => r.id}
            // FE-50: row-click opens approval. Cursor pointer signals interactivity.
            onRowClick={(r) => setSelectedId(r.id)}
            columns={[
              {
                id: 'subject',
                header: m.route_approvals_col_subject(),
                // FE-46 Pass 81: use BE.title (always populated) as primary subject.
                // Fall back to task_kind label если title missing. UUID prefix как dim caption.
                accessor: (r) => {
                  // F92 (Ф6): у заявки уровня 3 без номера документа заголовок — код строки
                  // матрицы; показываем название действия, а не код.
                  const subject =
                    r.tier === 3 && r.title === r.task_type
                      ? approvalActionLabel(r.task_type)
                      : r.title || (r as { subject?: string }).subject;
                  return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span>{subject || m.approval_task_kind({ kind: r.task_type })}</span>
                      <span className={shared.mono} style={{ fontSize: 11, opacity: 0.6 }}>
                        {r.id.slice(0, 8)}…
                      </span>
                    </div>
                  );
                },
              },
              ...(aggregateScope
                ? [
                    {
                      id: 'entity',
                      header: m.project_col_entity(),
                      accessor: (r: (typeof rows)[number]) =>
                        (r.entity_id ? entities[r.entity_id]?.name : undefined) ?? '—',
                      width: '180px',
                    },
                  ]
                : []),
              {
                id: 'task',
                header: m.route_approvals_col_task(),
                /**
                 * Гейт фазы B — русский интерфейс не показывает коды базы.
                 *
                 * Здесь стояло `project_cancel` строкой. Название действия — первой
                 * строкой словами, технический код — mono muted подстрочником, как
                 * идентификатор под «Предметом»: он нужен для разбора, а не для чтения.
                 */
                accessor: (r) => {
                  const human = r.addressee_user_id
                    ? approvalActionLabel(r.task_type)
                    : m.approval_task_kind({ kind: r.task_type });
                  return (
                    <div className={styles.cellStack}>
                      <span>{human}</span>
                      <span className={styles.cellCode}>{r.task_type}</span>
                    </div>
                  );
                },
              },
              {
                id: 'tier',
                header: m.route_approvals_col_tier(),
                accessor: (r) => (
                  <span
                    // Гейт фазы B — «уровень» это КЛАССИФИКАЦИЯ, а не отклонение.
                    // Семантическая заливка на нём тратит цвет, который в Calm UI
                    // означает «что-то не так» (STAITER_UI §1.3).
                    className={styles.tierChipNeutral}
                    data-tier={r.tier}
                    aria-label={
                      r.tier === 3
                        ? m.route_approvals_tier_aria_dual({ tier: r.tier })
                        : m.route_approvals_tier_aria({ tier: r.tier })
                    }
                  >
                    T{r.tier}
                  </span>
                ),
              },
              {
                id: 'sla',
                header: m.approval_queue_col_waiting(),
                // FE-49: SLA hierarchy through data-level tints + tabular-nums.
                // FE-14 fix (2026-05-31): BE возвращает `age_h` (canonical, см. api-contracts.ts:952),
                // legacy `age_hours` deprecated. Schema drift приводил к age=0 → flag всегда 'ok'.
                accessor: (r) => {
                  const ageH =
                    (r as { age_h?: number; age_hours?: number }).age_h ?? r.age_hours ?? 0;
                  /**
                   * Решение В-8 — у ЗАЯВКИ срока нет, поэтому и грамматики
                   * просроченного дедлайна у неё быть не может.
                   *
                   * Красная рамка вокруг «556ч» читалась как «просрочено» —
                   * состояние, которого у сущности без срока не существует. Обе
                   * половины гейта назвали это независимо. Возраст заявки подаётся
                   * ровным тоном и словами; согласованиям прогона агента (у них
                   * адресата нет, а SLA есть) прежняя шкала СОХРАНЯЕТСЯ — там
                   * «долго ждёт» действительно означает нарушенный порог.
                   */
                  const isRequestRow = Boolean(
                    (r as { addressee_user_id?: string | null }).addressee_user_id,
                  );
                  if (isRequestRow) {
                    // F30 (T2A-F16): у заявки срока нет (В-8) — сигнал не «истекло», а
                    // «дольше нормы», приглушённым предупреждением, а не красным.
                    const overNorm = (r as { overdue_norm?: boolean }).overdue_norm === true;
                    return (
                      <span
                        className={overNorm ? styles.slaNorm : styles.slaPlain}
                        data-level={overNorm ? 'norm' : 'ok'}
                        title={
                          overNorm
                            ? m.approval_overdue_norm({
                                days: String(sla.approval_requests.overdue_norm_working_days),
                              })
                            : undefined
                        }
                      >
                        {overNorm && <AlertTriangle size={12} aria-hidden="true" />}
                        {m.approval_age_waiting({ hours: ageH })}
                        {overNorm && (
                          <span className={shared.srOnly}>
                            {m.approval_overdue_norm({
                              days: String(sla.approval_requests.overdue_norm_working_days),
                            })}
                          </span>
                        )}
                      </span>
                    );
                  }
                  const lvl = slaLevel(ageH, r.sla);
                  return (
                    <span
                      // Возраст в норме — обычная числовая ячейка. Рамка-чип на
                      // каждом значении делает спокойное состояние заметным наравне
                      // с просроченным.
                      className={lvl === 'ok' ? styles.slaPlain : styles.slaPill}
                      data-level={lvl}
                      aria-label={m.route_approvals_sla_aria({ level: lvl })}
                    >
                      {m.route_approvals_sla_hours({ value: Math.round(ageH) })}
                    </span>
                  );
                },
              },
              // Заход 4 гейта — состав колонок зависит от вкладки.
              //
              // Во вкладке «мне на решение» колонка «Кому» повторяла самого читателя
              // в каждой строке, а «кто просит» не показывалась вовсе: колонка
              // занимала место и не отвечала ни на один вопрос. В истории не было ни
              // имени решившего, ни времени решения — «Согласовано» без ответа на
              // «кем и когда» не закрывает вопрос, ради которого в историю заходят.
              ...(tab === 'outbox'
                ? []
                : [
                    {
                      id: 'requester',
                      header: m.approval_queue_col_requester(),
                      accessor: (r: ApprovalItem) => {
                        const uid = (r as { requested_by_user_id?: string | null })
                          .requested_by_user_id;
                        if (!uid) return '—';
                        return <PersonCell user={q.data?.users?.[uid]} fallbackId={uid} />;
                      },
                      width: '220px',
                    },
                  ]),
              ...(tab === 'inbox'
                ? []
                : [
                    {
                      id: 'addressee',
                      header: m.approval_queue_col_addressee(),
                      accessor: (r: ApprovalItem) => {
                        const uid = (r as { addressee_user_id?: string | null }).addressee_user_id;
                        if (!uid) return '—';
                        return <PersonCell user={q.data?.users?.[uid]} fallbackId={uid} />;
                      },
                      // Почта не должна рваться внутри слова: ширина от контента.
                      width: '220px',
                    },
                  ]),
              ...(tab === 'history'
                ? [
                    {
                      id: 'decided',
                      header: m.approval_queue_col_decided(),
                      accessor: (r: ApprovalItem) => {
                        const uid = (r as { decided_by_user_id?: string | null })
                          .decided_by_user_id;
                        const at = (r as { decided_at?: string | null }).decided_at;
                        if (!uid && !at) return '—';
                        // Заход 5: имя решившего повторяло колонку «Кому» слово в слово —
                        // одна и та же строка дважды в одной строке таблицы. Имя нужно
                        // только когда решил НЕ адресат: после переадресации это разные
                        // люди, и вот тогда разница и есть содержание колонки.
                        const addressee = (r as { addressee_user_id?: string | null })
                          .addressee_user_id;
                        const showWho = Boolean(uid) && uid !== addressee;
                        return (
                          <div className={styles.cellStack}>
                            {showWho && uid ? (
                              <PersonCell user={q.data?.users?.[uid]} fallbackId={uid} />
                            ) : null}
                            {at ? <span className={styles.cellCode}>{formatDate(at)}</span> : null}
                          </div>
                        );
                      },
                      width: '220px',
                    },
                  ]
                : []),
              /**
               * Колонка «Срок» — только для согласований прогона агента.
               *
               * Решение В-8 сняло срок у заявки: `expires_at` у неё теперь пуст, и
               * колонка во вкладках «мне на решение» / «мои отправленные» была бы
               * столбцом прочерков — местом на экране, которое отвечает на вопрос,
               * снятый владельцем. Ответ «когда просрочится» заменён на «сколько
               * ждёт» в соседней колонке.
               */
              ...(tab === 'history'
                ? [
                    {
                      id: 'expires',
                      header: m.approval_queue_col_expires(),
                      accessor: (r: ApprovalItem) => {
                        const exp = (r as { expires_at?: string | null }).expires_at;
                        return exp ? <span className={shared.mono}>{formatDate(exp)}</span> : '—';
                      },
                      width: '150px',
                    },
                  ]
                : []),
              {
                id: 'status',
                header: m.route_approvals_col_status(),
                // FE-45: StatusBadge primitive (pill + icon + tinted bg) вместо plain text.
                accessor: (r) => {
                  // Подпись передаётся ЯВНО: без неё `StatusBadge` показывает свой
                  // словарь состояний прогона агента, и согласованная заявка
                  // подписывалась «Черновик готов» — статус на экране был неправдой.
                  const b = approvalStatusToBadge(r.status);
                  return <StatusBadge status={b.status} label={b.label} />;
                },
              },
              {
                id: 'open',
                header: '',
                // FE-50: chevron-right marker вместо «Открыть» button (row-click handles).
                accessor: () => (
                  <span className={styles.rowChevron} aria-hidden="true">
                    <ChevronRight size={16} />
                  </span>
                ),
              },
            ]}
          />
        )}
      </ListPageShell>

      <Drawer
        open={!!selected}
        onClose={() => setSelectedId(null)}
        title={
          (selected?.tier === 3 && selected.title === selected.task_type
            ? approvalActionLabel(selected.task_type)
            : selected?.title) ??
          (selected as { subject?: string } | null)?.subject ??
          m.route_approvals_drawer_title_fallback()
        }
        width={640}
        headerActions={
          // F29 круг 2 (UX, MAJOR): состояние заявки названо в шапке — у кого она
          // лежит. При расхождении последствий решение ждёт пересборки, а не
          // согласующего.
          selected &&
          isRequest &&
          (selected.status === 'waiting' || selected.status === 'pending') ? (
            // F96 (гейт Ф6, круг 1, K4 UI-2/UX-2): документ изменился после подписи №1 —
            // заявка ждёт пересборки, а не решающего; шапка говорит то же, что и тело.
            selected.tier3?.stale === true ? (
              <StatusBadge
                status="needs_review"
                size="sm"
                label={m.approvals_tier3_stale_badge()}
              />
            ) : selected.impact_changed === true ? (
              <StatusBadge
                status="needs_review"
                size="sm"
                label={
                  requestedByMe
                    ? m.approval_state_waits_you_rebuild()
                    : m.approval_state_waits_rebuild()
                }
              />
            ) : (
              <StatusBadge
                status="pending_review"
                size="sm"
                label={m.approval_state_waits_decider()}
              />
            )
          ) : undefined
        }
        footer={
          selected &&
          (() => {
            // FE-48: Tier 3 invariant — initiator не может self-approve.
            // F92 (Ф6): самоподпись — по user_id (раньше UUID сравнивался с почтой и не
            // совпадал никогда).
            const isSelfApprovalBlocked = selected.tier === 3 && requestedByMe;
            const pendingStatus = selected.status === 'pending' || selected.status === 'waiting';
            // FE-11: prevent double signature — current user уже в collected_signatures.
            const collected = selected.collected_signatures ?? [];
            const required = selected.required_signatures ?? 1;
            const alreadySigned =
              !!currentUserId && collected.some((s) => s.approver_id === currentUserId);
            const counterLabel = required > 1 ? ` (${collected.length}/${required})` : '';
            // F28 (T2A-F14; решение 15.09 — «блокирует»): снимок последствий разошёлся
            // с живым расчётом — подписывать по нему нельзя, пока заявку не пересобрали.
            const impactChanged = selected.impact_changed === true;
            const tier3Stale = selected.tier3?.stale === true;
            const tier3Offline =
              selected.tier3 !== undefined &&
              selected.subject_kind !== 'work_order' &&
              !selected.tier3.integration_connected;
            const blockReason = tier3Stale
              ? m.tier3_card_block_stale()
              : tier3Offline
                ? m.tier3_card_block_offline()
                : isSelfApprovalBlocked
                  ? m.approval_self_signature_blocked()
                  : alreadySigned
                    ? m.approval_already_signed_blocked()
                    : impactChanged
                      ? m.approval_impact_changed_blocked()
                      : undefined;
            /**
             * F5/F6 — инициатор своей заявки не решает её, а распоряжается ею:
             * переадресует или отзывает. Адресат — наоборот. Показывать оба набора
             * всем значило бы предлагать действия, которые сервер отклонит.
             *
             * Сами признаки считаются выше по компоненту: тело карточки объясняет
             * ту же выключенную кнопку словами, и второй копии условия быть не должно.
             */
            /**
             * Заход 4 гейта — решающие кнопки НЕ РЕНДЕРЯТСЯ тому, кто решать не может.
             *
             * Оба ревьюера независимо назвали это блокером: у инициатора самый
             * тяжёлый, залитый слот занимала недоступная «Согласовать и исполнить»,
             * а его собственные ходы были поданы самым слабым ghost-текстом —
             * вес кнопок обратен доступности. Выключенная кнопка в главном слоте
             * не защищает, она обещает право, которого нет.
             */
            const mayDecide = !isRequest || addressedToMe;
            return (
              <div className={styles.footer}>
                {/*
                  Заход 7 — слот пояснения РЕНДЕРИТСЯ В ОБЕИХ ВЕТКАХ, и один и тот же
                  элемент не прыгает по экрану между ролями: инициатору здесь стоит
                  «за кем решение», решающему — под чьим именем он подписывает. У
                  адресата эта строка жила в теле, в ~470px от кнопки, которую она
                  объясняет, — при длинной разнице имя уезжало из поля зрения ровно
                  в момент нажатия.
                */}
                {/**
                 * Раунд 4 гейта T2A-F4 — обе половины независимо назвали одно и то
                 * же: имя держателя стояло ДВАЖДЫ на одной панели, в поле «Решает»
                 * (появилось раундом 3) и первым словом подвала. Из-за лишнего
                 * префикса подвальная строка переносилась на вторую строку.
                 *
                 * Подвал теперь несёт только ПРАВА читателя — что он может сделать
                 * с этой заявкой; имя живёт в мете, где ему и место. Стороннему
                 * читателю (не инициатору и не адресату) имя в подвале остаётся:
                 * поля «Решает» у него нет, и без имени строка стала бы отказом без
                 * адресата.
                 */}
                {/* Круг 2 (K4 UI-2): у устаревшей заявки уровня 3 тревога уже в шапке и
                    в теле — в подвале её не повторяем. */}
                {isRequest && !addressedToMe && addresseeLabel && !(isTier3 && tier3Stale) && (
                  <span className={styles.footerNote}>
                    <ShieldCheck size={13} aria-hidden="true" />
                    <span>
                      {requestedByMe
                        ? isTier3 && tier3Stale
                          ? m.approvals_tier3_requester_rebuild_hint()
                          : selected.impact_changed === true
                            ? m.approval_decide_requester_rebuild_hint()
                            : m.approval_decide_requester_hint()
                        : `${m.approval_pending_addressee({ name: addresseeLabel })} · ${m.error_by_code('APPROVAL_NOT_ADDRESSEE')}`}
                    </span>
                  </span>
                )}
                {currentUser && (!isRequest || addressedToMe) && (
                  <span className={styles.footerNote}>
                    <ShieldCheck size={13} aria-hidden="true" />
                    <span>
                      {m.route_approvals_you_signing_prefix()}{' '}
                      <strong>{currentUser.full_name ?? currentUser.email}</strong> (
                      {getRoleLabel(currentUser.role as string)}).
                    </span>
                  </span>
                )}
                {/* F34 (F28 со стороны решающего): выключенная «Согласовать» объясняет
                    причину видимым текстом рядом с кнопкой, а не только подсказкой. */}
                {/* Круг 3 (K4 UI-1): у выключенной «Поставить вторую подпись» причина рядом —
                    одной приглушённой строкой, без повторной тревоги. */}
                {mayDecide && isTier3 && tier3Stale && (
                  <span id="tier3-approve-reason" className={styles.footerReason}>
                    {m.approvals_tier3_waits_rebuild()}
                  </span>
                )}
                {mayDecide && tier3Offline && !tier3Stale && (
                  <span className={styles.footerNote}>
                    <AlertTriangle size={13} aria-hidden="true" />
                    <span>{blockReason}</span>
                  </span>
                )}
                {mayDecide && impactChanged && (
                  <span className={styles.footerNote}>
                    <AlertTriangle size={13} aria-hidden="true" />
                    <span>{m.approval_impact_changed_blocked()}</span>
                  </span>
                )}
                <div className={styles.footerActions}>
                  {isRequest && requestedByMe && pendingStatus && (
                    <>
                      {/**
                       * Решение владельца В-9: **одна позиция — один смысл**.
                       *
                       * Правый нижний слот дровера — всегда ПОЗИТИВНОЕ действие роли.
                       * Здесь стояло «Отозвать заявку» в деструктивном тоне, и одна и
                       * та же позиция несла у адресата «Согласовать и исполнить», а у
                       * инициатора — красный отзыв. Два ревьюера разошлись об этот
                       * слот в противоположные стороны, и спор решил владелец в
                       * пользу стороны UX.
                       *
                       * Позитивный ход инициатора — «Переадресовать»: заявка идёт
                       * дальше, к тому, кто может решить. Отзыв — штатное «передумал»,
                       * а не разрушение, поэтому он вторичный и без красного тона:
                       * красным на этом экране помечено ОТКЛОНЕНИЕ, и третий смысл у
                       * того же цвета обесценивает первые два.
                       */}
                      {/* F96 (K4 UX-4): у устаревшей заявки уровня 3 ход инициатора один —
                          пересборка (она и отзывает); отзыв и переадресация рядом с ней спорили. */}
                      {/* Круг 2 (K4 UX-5): отзыв без пересборки — вторичный ход и у
                          устаревшей заявки («отмена уже не нужна»); переадресация — нет. */}
                      <Button
                        variant="secondary"
                        loading={withdrawMut.isPending}
                        onClick={() => withdrawMut.mutate(selected.id)}
                      >
                        {m.approval_withdraw_cta()}
                      </Button>
                      {/**
                       * Раунд 3 — обе половины независимо назвали залитую
                       * «Переадресовать» ошибкой веса: UI — «акцентную заливку
                       * забрала более мягкая кнопка», UX — «выделено самое редкое
                       * и самое чреватое действие, смена решающего в обход
                       * маршрута».
                       *
                       * Решение В-9 при этом НЕ пересматривается — оно применяется
                       * своей главной фразой: «у инициатора слот ПУСТ». Скобка
                       * «(займёт „Переадресовать“, когда появится)» — прогноз
                       * владельца, а не указание; переадресация в коде уже была, и
                       * прочтение скобки как инструкции поставило акцент на ход,
                       * который инициатор совершает реже всего. Оба действия
                       * вторичны, залитой кнопки у инициатора нет вовсе.
                       */}
                      {!(isTier3 && tier3Stale) && (
                        <Button variant="secondary" onClick={() => setReassignOpen(true)}>
                          {m.approval_reassign_cta()}
                        </Button>
                      )}
                      {/* F29 круг 2 (UX, MAJOR): при расхождении последствий заявка стоит
                          на инициаторе — пересборка и есть его ход, она занимает правый
                          слот (В-9: «правый слот — позитивное действие роли»). Вне этого
                          состояния слот пуст, как решено кругом 3 T2A-F4. */}
                      {isTier3 && tier3Stale && (
                        <Button
                          variant="primary"
                          leadingIcon={<RefreshCw size={14} />}
                          loading={staleRebuildMut.isPending}
                          onClick={() => {
                            const href = tier3SubjectHref({
                              subjectKind: selected.subject_kind,
                              subjectId: selected.subject_id,
                              taskType: selected.task_type,
                              payload: selected.payload,
                            });
                            staleRebuildMut.mutate({ id: selected.id, href });
                          }}
                        >
                          {m.tier3_card_rebuild()}
                        </Button>
                      )}
                      {selected.impact_changed === true && (
                        <Button
                          variant="primary"
                          leadingIcon={<RefreshCw size={14} />}
                          loading={rebuildMut.isPending}
                          disabled={rebuildMut.isPending}
                          onClick={() => rebuildMut.mutate(selected.id)}
                        >
                          {m.approval_impact_rebuild_cta()}
                        </Button>
                      )}
                    </>
                  )}
                  {mayDecide && (
                    <>
                      {/*
                        FE-52: третье действие — запросить уточнение.

                        Раунд 2 гейта T2A-F4: было `ghost`, а по §4.1 STAITER_UI ghost
                        означает «Отмена, навигация». Уточнение — такой же ход по
                        заявке, как отклонение: адресат им РЕШАЕТ не решать сейчас.
                        Самый слабый вес подсказывал, что это выход со страницы.
                      */}
                      <Button
                        variant="secondary"
                        onClick={() => {
                          setRejectOpen(true);
                        }}
                        disabled={!pendingStatus}
                      >
                        {m.approval_request_changes_btn()}
                      </Button>
                      <Button
                        variant="destructive"
                        onClick={() => {
                          setRejectOpen(true);
                        }}
                        disabled={!pendingStatus}
                      >
                        {m.route_approvals_btn_reject()}
                      </Button>
                      <Button
                        variant="primary"
                        leadingIcon={<ShieldCheck size={14} />}
                        disabled={
                          !pendingStatus ||
                          approveMut.isPending ||
                          isSelfApprovalBlocked ||
                          alreadySigned ||
                          impactChanged ||
                          tier3Stale ||
                          tier3Offline
                        }
                        loading={approveMut.isPending}
                        onClick={() => approveMut.mutate(selected.id)}
                        title={blockReason}
                        aria-describedby={
                          isTier3 && tier3Stale ? 'tier3-approve-reason' : undefined
                        }
                      >
                        {/* F96 (K4 UI-1/UX-5): адресат уровня 3 ставит вторую, последнюю
                            подпись — «(1/2)» читалось как «первая из двух». */}
                        {isTier3
                          ? m.approvals_tier3_sign_second()
                          : isRequest
                            ? m.approval_decide_approve()
                            : m.route_approvals_btn_approve()}
                        {isTier3 ? '' : counterLabel}
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })()
        }
      >
        {selected && (
          <div className={styles.drawer}>
            {/* F92 (Ф6): заявка уровня 3 — снимок, документ сейчас, шкала подписей, причины. */}
            {selected.tier === 3 && (
              <Tier3ApprovalBlock
                item={selected}
                requestedByMe={requestedByMe}
                addressedToMe={addressedToMe}
                addresseeLabel={addresseeLabel}
              />
            )}

            {/**
             * F4 — карточка показывает ТЕЛО НАМЕРЕНИЯ: что именно произойдёт при
             * согласовании. Решать «да/нет» по названию типа — это подписывать
             * вслепую, ровно то, от чего пак уходит.
             */}
            {(() => {
              const payload = (selected as { payload?: Record<string, unknown> }).payload;
              const addressee = (selected as { addressee_user_id?: string | null })
                .addressee_user_id;
              if (!addressee || selected.tier === 3) return null;
              const reason = typeof payload?.['reason'] === 'string' ? payload['reason'] : null;
              return (
                <section className={styles.drawerBlock}>
                  {/* F33 (T2A-F19): метка секции у всех четырёх блоков одна и стоит над
                      блоком, а не внутри заливки. */}
                  <h3 className={shared.sectionTitle}>{m.approval_decide_intent_title()}</h3>
                  <div className={styles.intent}>
                    <p className={styles.intentAction}>
                      {approvalActionLabel(selected.task_type)}
                      {selected.title ? ` · ${selected.title}` : ''}
                    </p>
                    {reason && <p className={styles.intentReason}>{reason}</p>}
                    {(() => {
                      const days = planShiftDays(
                        (
                          selected as {
                            diff?: {
                              before?: Record<string, unknown>;
                              after?: Record<string, unknown>;
                            };
                          }
                        ).diff ?? null,
                      );
                      if (days === null) return null;
                      return (
                        <p className={styles.intentShift}>
                          {m.approval_plan_shift_summary({
                            days: Math.abs(days),
                            sign: days > 0 ? '+' : '−',
                          })}
                        </p>
                      );
                    })()}
                    {/* Строка «Срок заявки: …» снята решением В-8 вместе с самим
                      сроком: заявка живёт до решения адресата или отзыва
                      инициатором. Сколько она ждёт — сказано полем «Возраст» ниже
                      и колонкой очереди. */}
                    {/**
                     * Раунд 3, UX-половина, 🔴 BLOCKER — и она права против решения
                     * пятого захода («ссылки, которая заведомо не откроется, лучше
                     * не показывать вовсе»).
                     *
                     * SSOT говорит обратное, причём дважды: `docs/STAITER_UX.md:59`
                     * — «Unreachable action — не скрыт, а „неавторизован“ с указанием
                     * кто approver»; `:1094` — «RBAC unreachable → visible с
                     * объяснением, не спрятано». Спрятанная ссылка убирала у
                     * РЕШАЮЩЕГО единственную точку проверки предмета и не оставляла
                     * ему даже знания, что такая точка существует.
                     *
                     * 403 в момент решения по-прежнему недопустим, поэтому переход не
                     * восстанавливается — восстанавливается ЗНАНИЕ: место видно,
                     * неактивно, и рядом сказано, чего не хватает.
                     */}
                    {(selected as { subject_id?: string }).subject_id &&
                      (mayOpenSubject ? (
                        <a
                          className={shared.link}
                          href={`/projects/${(selected as { subject_id?: string }).subject_id}`}
                        >
                          {m.approval_queue_open_subject()}
                        </a>
                      ) : (
                        /* §4.4 STAITER_UI: состояние опознаётся значком и текстом, а
                         не одним только грейдом. Замок отличает «закрыто правами»
                         от «просто подпись мельче». */
                        /* F32 (T2A-F18; В-24, B42): отказ называет, КТО выдаёт доступ, по
                           имени, и даёт ход — просьбу о доступе тем же компонентом, что
                           экран модульного отказа (F5/F10). */
                        <div className={styles.subjectLocked}>
                          <Lock size={14} aria-hidden="true" />
                          <div className={styles.subjectLockedBody}>
                            <span>{m.approval_subject_link_no_module_named()}</span>
                            <AccessGrantorsInline />
                            <RequestAccessButton
                              variant="secondary"
                              projectId={(selected as { subject_id?: string }).subject_id}
                              module="tasks"
                            />
                          </div>
                        </div>
                      ))}
                  </div>
                </section>
              );
            })()}

            {/**
             * Заход 4, блокер UX: решающий обязан знать о последствиях НЕ МЕНЬШЕ
             * просящего. Тот же блок, что стоит в диалоге инициатора, — здесь он
             * считается на момент решения, а не на момент отправки заявки.
             */}
            {isRequest &&
              selected.task_type === 'project_cancel' &&
              (selected as { subject_id?: string }).subject_id &&
              (selected as { impact?: Record<string, number> | null }).impact && (
                <section className={styles.drawerBlock}>
                  <h3 className={shared.sectionTitle}>{m.approval_impact_title()}</h3>
                  <CancelImpactList
                    projectId={(selected as { subject_id?: string }).subject_id as string}
                    // F29 круг 1 (UX, MAJOR): при расхождении первым стоит расчёт на
                    // сейчас, а снимок заявки — строкой «при отправке» в блоке ниже.
                    // Устаревшее «1 задача будет отменена» было главным фактом блока.
                    snapshot={
                      (selected.impact_changed === true && selected.impact_now
                        ? selected.impact_now
                        : (selected as { impact?: Record<string, number> | null })
                            .impact) as unknown as CancelImpactSnapshot
                    }
                    snapshotLive={selected.impact_changed === true}
                    // Два соседних заголовка об одном («что произойдёт при
                    // согласовании» над телом заявки и «что произойдёт после
                    // подтверждения» над последствиями) читались как повтор, и
                    // первый подменял содержимое второго: тело заявки — это НЕ
                    // последствия. Разведены по смыслу: «Заявка» и «Последствия».
                    // F33: заголовок — меткой секции над блоком; янтарь остаётся полосе.
                    title={null}
                    snapshotNote
                    snapshotRebuilt={Boolean(selected.impact_rebuilt_at)}
                    drift={
                      selected.impact_changed === true ? (
                        <ApprovalImpactDrift
                          before={selected.impact ?? null}
                          now={selected.impact_now ?? null}
                          canRebuild={addressedToMe || requestedByMe}
                          viewer={requestedByMe ? 'requester' : 'decider'}
                          actionInFooter={requestedByMe}
                          sentAt={
                            selected.impact_rebuilt_at ??
                            (selected as { requested_at?: string | null }).requested_at ??
                            null
                          }
                          pending={rebuildMut.isPending}
                          onRebuild={() => rebuildMut.mutate(selected.id)}
                        />
                      ) : undefined
                    }
                    /* Карточка отдаёт `requested_at`, строка списка — `created_at`;
                     дровер сливает оба источника, и брать надо тот, что пришёл. */
                    snapshotAt={
                      selected.impact_rebuilt_at ??
                      (selected as { requested_at?: string | null }).requested_at ??
                      (selected as { created_at?: string | null }).created_at ??
                      null
                    }
                  />
                </section>
              )}

            {/* F29 круг 1 (UX, MAJOR): ответ на «что произойдёт» — последствия и разница
                подряд; сведения о заявке — после них. */}
            {/* F92 (Ф6): у заявки уровня 3 «разницы» нет — что подписано и что с документом
                сейчас показывает карточка уровня 3 выше. */}
            {selected.diff && selected.tier !== 3 && (
              <section className={styles.drawerBlock}>
                <h3 className={shared.sectionTitle}>{m.route_approvals_section_diff()}</h3>
                {/* T2A-F15 (F29): в diff идут сырые коды — сравнение по значению, а не
                    по подписи; исход и стадия рисуются бейджем с тоном реестра. */}
                <ApprovalDiff
                  before={selected.diff.before}
                  after={selected.diff.after}
                  labels={diffFieldLabels(selected.diff.after, selected.diff.before)}
                  renderValue={renderDiffValue}
                />
              </section>
            )}

            <section className={styles.drawerBlock}>
              <h3 className={shared.sectionTitle}>{m.route_approvals_section_meta()}</h3>
              <dl className={styles.meta}>
                <div>
                  <dt>{m.route_approvals_meta_task()}</dt>
                  {/* Заход 4 — в карточке действие называется словами, как в таблице.
                    Код операции остаётся подстрочником для разбора. */}
                  <dd>
                    {isRequest ? (
                      <span className={styles.cellStack}>
                        <span>{approvalActionLabel(selected.task_type)}</span>
                        {/* F96 (K4 UI-4): код операции уровня 3 пользователю не нужен. */}
                        {!isTier3 && <span className={styles.cellCode}>{selected.task_type}</span>}
                      </span>
                    ) : (
                      m.approval_task_kind({ kind: selected.task_type })
                    )}
                  </dd>
                </div>
                <div>
                  <dt>{m.route_approvals_meta_tier()}</dt>
                  <dd className={shared.mono}>T{selected.tier}</dd>
                </div>
                {/**
                 * Раунд 3, UI: во вкладке «мои отправленные» слот «Запросил» называл
                 * самого читателя, а держатель заявки — только сноской в подвале.
                 * Главный вопрос отправителя («у кого она лежит») стоял мелким
                 * шрифтом, а место занимал ответ, который он знает.
                 */}
                {/* F31 (T2A-F17; B41): инициатору — маршрут целиком (кто решает сейчас,
                    кто дальше, сколько шагов), а не одно имя держателя. */}
                {/* F96 (K4 UI-3): у заявки уровня 3 маршрут — это шкала подписей блока
                    выше; второй показ из другой модели («Шаг 1 из 1») расходился с ней. */}
                {isRequest && requestedByMe && !isTier3 && (selected.route?.length ?? 0) > 0 ? (
                  <div className={styles.metaWide}>
                    <dt>{m.approval_meta_route()}</dt>
                    <dd>
                      <ApprovalRoute
                        route={selected.route ?? []}
                        currentStep={selected.current_step}
                        waitingRebuild={selected.impact_changed === true}
                      />
                    </dd>
                  </div>
                ) : isRequest && requestedByMe && addresseeLabel ? (
                  <div>
                    <dt>{m.approval_meta_decider()}</dt>
                    <dd>{addresseeLabel}</dd>
                  </div>
                ) : (
                  <div>
                    <dt>{m.route_approvals_meta_requested_by()}</dt>
                    {/* S-FIX-2-FE.3.3: показ UUID если BE.4.1 catch вернул пустой userMap.
                    requested_by — может быть UUID или email; шортим если выглядит как UUID. */}
                    <dd>
                      {(() => {
                        // Сокращённый идентификатор человек расшифровать не может.
                        // Имя (если есть) → почта из денорм-набора → и только потом id.
                        // Имя из самой карточки первым: по ссылке с проекта денорм-набор
                        // списка пуст, и без него экран называл инициатора обрубком id.
                        const fromDetail = (selected as { requested_by_name?: string | null })
                          .requested_by_name;
                        if (fromDetail) return fromDetail;
                        const rid =
                          (selected as { requested_by_user_id?: string | null })
                            .requested_by_user_id ?? selected.requested_by;
                        if (!rid) return '—';
                        const u = q.data?.users?.[rid] as
                          | { name?: string | null; email?: string }
                          | undefined;
                        if (u?.name || u?.email) return u.name || u.email;
                        return rid.length === 36 && rid.includes('-') ? `${rid.slice(0, 8)}…` : rid;
                      })()}
                    </dd>
                  </div>
                )}
                <div
                  className={
                    isRequest && (requestedByMe || selected.overdue_norm === true)
                      ? styles.metaWide
                      : undefined
                  }
                >
                  <dt>{m.route_approvals_meta_age()}</dt>
                  {/* У заявки возраст читается словами (В-8) и потому НЕ моноширинный:
                    моноширь здесь означала бы «это число, которое сравнивают
                    столбиком», а «ждёт 23 дня» сравнивают глазом. Согласованию
                    прогона агента цифра в часах остаётся. */}
                  <dd className={isRequest ? undefined : shared.mono}>
                    {isRequest
                      ? m.approval_age_waiting({ hours: selected.age_hours ?? 0 })
                      : m.route_approvals_meta_age_hours({
                          value: Math.round(selected.age_hours ?? 0),
                        })}
                    {/* F30 (T2A-F16): после нормы — приглушённое предупреждение и ход
                        инициатора рядом с полем. Решающему напоминать некому. */}
                    {isRequest && selected.overdue_norm === true && (
                      <span className={styles.normNote}>
                        <AlertTriangle size={14} aria-hidden="true" />
                        {m.approval_overdue_norm({
                          days: String(sla.approval_requests.overdue_norm_working_days),
                        })}
                      </span>
                    )}
                    {isRequest &&
                      requestedByMe &&
                      selected.tier3?.stale !== true &&
                      (selected.status === 'waiting' || selected.status === 'pending') &&
                      (() => {
                        const rs = remindState(
                          selected.last_reminded_at,
                          new Date(),
                          sla.approval_requests.remind_min_interval_hours,
                        );
                        return (
                          <span className={styles.remindRow}>
                            <Button
                              size="sm"
                              variant="secondary"
                              leadingIcon={<BellRing size={14} />}
                              // F29 круг 2 (UX, BLOCKER): пока последствия расходятся,
                              // согласующий решить не может — напоминание вело бы его
                              // в тупик. Сначала пересборка.
                              disabled={
                                !rs.allowed ||
                                remindMut.isPending ||
                                selected.impact_changed === true
                              }
                              loading={remindMut.isPending}
                              onClick={() => remindMut.mutate(selected.id)}
                            >
                              {m.approval_remind_cta()}
                            </Button>
                            {selected.impact_changed === true ? (
                              <span className={styles.remindWhen}>
                                {m.approval_remind_blocked_drift()}
                              </span>
                            ) : (
                              selected.last_reminded_at && (
                                <span className={styles.remindWhen}>
                                  {rs.nextAt
                                    ? m.approval_remind_last_next({
                                        last: formatDate(selected.last_reminded_at),
                                        next: formatDate(rs.nextAt.toISOString()),
                                      })
                                    : m.approval_remind_last({
                                        last: formatDate(selected.last_reminded_at),
                                      })}
                                </span>
                              )
                            )}
                          </span>
                        );
                      })()}
                  </dd>
                </div>
                {selected.run_id && (
                  <div>
                    <dt>{m.route_approvals_meta_run()}</dt>
                    <dd>
                      <a href={`/runs/${selected.run_id}`} className={shared.link}>
                        {selected.run_id.slice(0, 8)}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </section>

            {/* FE-13: AI Insight Card explainer about the proposed change.
                Для заявки-намерения НЕ рендерится: её объяснение уже стоит выше
                блоком «что произойдёт при согласовании», а этот текст говорит про
                складской L1 и compensating entry — к отмене проекта отношения не
                имеющие. Два объяснения подряд, одно из которых неверное, хуже одного. */}
            {!(selected as { addressee_user_id?: string | null }).addressee_user_id && (
              <AIInsightCard
                title="Что произойдёт после одобрения"
                body={
                  selected.tier === 3
                    ? `После сбора ${selected.required_signatures ?? 2} подписей — push в 1С / налоговую. Изменение необратимое; rollback требует compensating push.`
                    : 'После одобрения изменение применяется к L1 (STAITER stock ledger / финансы). Доступен compensating entry для отмены.'
                }
              />
            )}

            {(selected.evidence_refs ?? []).length > 0 && (
              <>
                <h3 className={shared.sectionTitle}>{m.route_approvals_section_evidence()}</h3>
                <div className={styles.evidenceChips}>
                  {(selected.evidence_refs ?? []).map((ref) => (
                    <ProvenanceChip
                      key={ref.id}
                      source={ref.source}
                      document_id={ref.document_id}
                      fetched_at={ref.fetched_at}
                    />
                  ))}
                </div>
              </>
            )}

            {selected.tier !== 3 && (selected.collected_signatures ?? []).length > 0 && (
              <>
                <h3 className={shared.sectionTitle}>{m.route_approvals_section_signatures()}</h3>
                <ul className={styles.sigList}>
                  {(selected.collected_signatures ?? []).map((s, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: signature order stable per approval
                    <li key={`sig-${i}`}>
                      <strong>{s.approver_name}</strong>
                      <span className={shared.subtle}>{s.role}</span>
                      <span className={shared.mono}>{s.signed_at}</span>
                      {s.note && <span>{s.note}</span>}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {/* FE-14 honest-audit: pending approvers ladder.
                Показывает сколько ещё подписей нужно + counter (1/2 collected). */}
            {(() => {
              const collected = (selected.collected_signatures ?? []).length;
              const required = selected.required_signatures ?? 1;
              const pending = required - collected;
              if (pending <= 0) return null;
              // У АДРЕСНОЙ заявки подписант один и назван по имени: «ожидается
              // подпись №1» — язык прежней модели с общей очередью, и рядом с именем
              // адресата он читается как вторая, несуществующая ступень.
              if ((selected as { addressee_user_id?: string | null }).addressee_user_id) {
                return null;
              }
              return (
                <>
                  <h3 className={shared.sectionTitle}>
                    {m.route_approvals_section_pending({ collected, required })}
                  </h3>
                  <ul className={styles.sigList}>
                    {Array.from({ length: pending }, (_, i) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: pending order is positional only
                      <li key={`pending-${i}`} className={shared.subtle}>
                        <span>•</span>
                        <span>
                          {m.route_approvals_pending_slot({ position: collected + i + 1 })}
                        </span>
                      </li>
                    ))}
                  </ul>
                </>
              );
            })()}
          </div>
        )}
      </Drawer>

      {/* F5 — переадресация: адресат в отпуске, а решение нужно. Молчаливого
          перехвата нет по решению владельца, поэтому ход явный и виден в аудите. */}
      <Dialog
        open={reassignOpen}
        onClose={() => setReassignOpen(false)}
        title={m.approval_reassign_title()}
        footer={
          <div className={styles.footer}>
            <Button variant="ghost" onClick={() => setReassignOpen(false)}>
              {m.common_button_cancel()}
            </Button>
            <Button
              variant="primary"
              disabled={!reassignTo || reassignMut.isPending}
              loading={reassignMut.isPending}
              onClick={() => {
                if (selected) reassignMut.mutate({ id: selected.id, to: reassignTo });
              }}
            >
              {m.approval_reassign_cta()}
            </Button>
          </div>
        }
      >
        {/* Пикер монтируется ТОЛЬКО с открытым диалогом: иначе его запрос кандидатов
            уходит на каждом заходе в очередь, а роль, у которой нет модуля проектов
            (обычный адресат — контролёр), получает 403 и тост «нет доступа» на пустом
            месте. Поймано живым прогоном. */}
        {reassignOpen && selected?.tier === 3 && (
          <Tier3CosignerPicker
            value={reassignTo}
            onChange={setReassignTo}
            taskType={selected.task_type}
            subjectKind={selected.subject_kind ?? ''}
            payload={selected.payload ?? {}}
          />
        )}
        {reassignOpen && selected?.tier !== 3 && (
          <ApproverPicker
            value={reassignTo}
            onChange={setReassignTo}
            taskType={selected?.task_type ?? ''}
          />
        )}
        <p className={shared.subtle}>{m.approval_reassign_hint()}</p>
      </Dialog>

      <Dialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        title={m.route_approvals_reject_dialog_title()}
        footer={
          <div className={styles.footer}>
            <Button variant="ghost" onClick={() => setRejectOpen(false)}>
              {m.common_button_cancel()}
            </Button>
            <Button
              variant="destructive"
              disabled={!rejectNote.trim() || rejectMut.isPending}
              loading={rejectMut.isPending}
              onClick={() => {
                if (selected) rejectMut.mutate({ id: selected.id, note: rejectNote.trim() });
              }}
            >
              {m.route_approvals_btn_reject()}
            </Button>
          </div>
        }
      >
        {/* FE-52 Sprint N+: multi-line reason textarea (PM пишет 2-4 строки контекста). */}
        <Textarea
          label={m.route_approvals_reject_reason_label()}
          value={rejectNote}
          onChange={(e) => setRejectNote(e.target.value)}
          placeholder={m.route_approvals_reject_reason_placeholder()}
          hint={m.route_approvals_reject_reason_hint()}
          rows={3}
        />
      </Dialog>
    </div>
  );
}
